import { query, mutate } from '@sylchi/keystone-graphql-client';
import Router from 'src/router/index';
import Store from '.';
import { campaignFullDetails } from './fragments';
import dateformat from 'dateformat';
import moment from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

moment.extend(utc);
moment.extend(timezone);

const backendUrl = 'https://partnerapi.bankos.io'

export default {
  login: async ({ state, commit, dispatch }, { email }) => {
    let problems = false;
    try {
      const { success } = await fetch(backendUrl + "/auth/login", {
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify({ email }),
      }).then((res) => res.json());
      if (success) {
        sessionStorage.setItem('email', email);
        Router.push('/auth/verify');
      }
    } catch (err) {
      console.error(err);
    }
    return problems;
  },
  verify: async ({ state, commit, dispatch }, { email, otp }) => {
    let problems = false;
    try {
      const { success } = await fetch(backendUrl + "/auth/verifyCode", {
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
        credentials: 'include',
        body: JSON.stringify({ email, otp }),
      }).then((res) => res.json());
      if (success) {
        await dispatch('getAuthenticatedUser');
        Router.push({ name: 'aff_overview' });
      }
    } catch (err) {
      console.error(err);
    }
    return problems;
  },
  register: async ({ dispatch }, data) => {
    try {
      const {
        data: { user },
      } = await mutate({
        mutation: `
          mutation($data: UserCreateInput!){
              user: createUser(data: $data){
                id
              }
          }
        `,
        variables: { data },
      });
      if (user) {
        return dispatch('login', {
          email: data.email
        });
      }
    } catch (err) {
      console.error(err);
    }
    return false;
  },
  getAuthenticatedUser: async ({ commit }) => {
    try {
      let user = await fetch(backendUrl + '/auth/authenticateduser', {
        credentials: 'include',
      }).then(res => res.json());
      if(user.success === false) user = null;
      commit('setUserData', user);
      if (Store.state.returnRoute) {
        Router.push(Store.state.returnRoute.path);
        Store.commit('setReturnRoute', null);
      }
      return user !== null;
    } catch (err) {
      console.error(err);
    }
    return false;
  },
  fetchCampaigns: async ({ commit }) => {
    const { data } = await query({
      query: `
        query{
            allCampaigns(sortBy: status_ASC){
              id
              name
              status
              targetCountries{
                iso2
              }
              campaignImage{
                publicUrl
              }
              userPrices{
                model
                price
              }
              stats{
                cr
                epc
              }
            }
        }
      `,
    });
    commit('setCampaigns', data.allCampaigns);
  },
  logout: async ({ commit }) => {
    try {
      const { success } = await fetch(backendUrl + '/auth/logout', { credentials: 'include' }).then(res => res.json());
      if (success) {
        commit('setUserData', null);
        window.location.href = '/auth/login';
        return true;
      }
    } catch (err) {
      console.error(err);
    }
    return false;
  },
  fetchCampaign: async ({ commit }, { id }) => {
    try {
      const monthAgo = new Date();
      monthAgo.setDate(monthAgo.getDate() - 30);
      const startDate = dateformat(monthAgo, 'UTC:yyyy-mm-dd');
      const endDate = dateformat(Date.now(), 'UTC:yyyy-mm-dd');
      const { data } = await query({
        query: `
          query{
           Campaign(where: { id: "${id}" } ) {
              ...campaignFullDetails
              stats(startDate: "${startDate}", endDate: "${endDate}"){
                cr
                epc
              }
            }
          }
          ${campaignFullDetails}
        `,
      });
      if (data?.Campaign) {
        commit('setCampaign', data.Campaign);
        return true;
      }
    } catch (err) {
      console.error(err);
    }
    return false;
  },
  fetchUserAggregations: async (
    { state, commit, dispatch },
    conversionTypes = []
  ) => {
    if (state.selectedCampaigns.length > 0) dispatch('fetchClicksByCampaign');
    try {
      const { data } = await query({
        query: `
            query{
              allUserDayAggregations(startDate: "${
                state.dateFrom
              }", endDate: "${state.dateTo}", user: "${
          state.user.userLevel === 'ADMIN' && state.selectedUser
            ? state.selectedUser.value
            : state.user.id
        }" ) {
                clicks
                conversions
                earnings
                conversionRate
                epc
                date
              }
            }
          `,
      });
      commit('setUserAggregations', data.allUserDayAggregations);
      if (state.selectedCampaigns.length === 0)
        commit(
          'setClicksByCampaign',
          data.allUserDayAggregations.reduce((a, b) => (a += b.clicks), 0)
        );
      return;
    } catch (err) {
      console.error(err);
    }
  },
  fetchClicksByCampaign: async ({ state, commit }) => {
    const variables = {
      data: {
        createdAt_gte: moment
          .tz(state.dateFrom, 'Europe/Tallinn')
          .startOf('day')
          .toDate()
          .toISOString(),
        createdAt_lt: moment
          .tz(state.dateTo, 'Europe/Tallinn')
          .endOf('day')
          .toDate()
          .toISOString(),
        publisher: {
          id:
            state.user.userLevel === 'ADMIN' && state.selectedUser
              ? state.selectedUser.value
              : state.user.id,
        },
      },
    };
    if (state.selectedCampaigns.length > 0) {
      if (state.selectedCampaigns.length === 1) {
        variables.data.campaign = { id: state.selectedCampaigns[0].value };
      } else {
        variables.data.campaign = {
          id_in: state.selectedCampaigns.map((item) => item.value),
        };
      }
    }
    try {
      const { data } = await query({
        query: `
            query($data: ClickWhereInput!){
              _allClicksMeta(where: $data){
                count
              }
            }
          `,
        variables,
      });
      commit('setClicksByCampaign', data._allClicksMeta.count);
    } catch (err) {
      console.error(err);
    }
  },
  fetchUserConversions: async ({ state, commit }, conversionTypes = []) => {
    const variables = {
      data: {
        createdAt_gte: moment
          .tz(state.dateFrom, 'Europe/Tallinn')
          .startOf('day')
          .toDate()
          .toISOString(),
        createdAt_lt: moment
          .tz(state.dateTo, 'Europe/Tallinn')
          .endOf('day')
          .toDate()
          .toISOString(),
        publisher: {
          id:
            state.user.userLevel === 'ADMIN' && state.selectedUser
              ? state.selectedUser.value
              : state.user.id,
        },
      },
    };
    if (state.selectedCampaigns.length > 0) {
      if (state.selectedCampaigns.length === 1) {
        variables.data.campaign = { id: state.selectedCampaigns[0].value };
      } else {
        variables.data.campaign = {
          id_in: state.selectedCampaigns.map((item) => item.value),
        };
      }
    }
    if (conversionTypes.length > 0) {
      variables.data.campaignModel_in = conversionTypes.map((item) =>
        item.toLowerCase()
      );
    }
    try {
      const { data } = await query({
        query: `
          query($data: ConversionWhereInput!){
            allConversions(where: $data, sortBy: createdAt_DESC){
              id
              createdAt
              campaign{
                name
              }
              click{
                id
                urlParams
              }
              campaignModel
              publisherPrice
              publisherStatus              
            }
          }
        `,
        variables,
      });
      commit('setUserConversions', data.allConversions);
      return;
    } catch (err) {
      console.error(err);
    }
  },
  fetchNotifications: async ({ state, commit }) => {
    try {
      const { data } = await query({
        query: `
          query($data: NotificationWhereInput!){
            allNotifications(where: $data, sortBy: createdAt_DESC){
              id
              title: name
              content
              createdAt
              campaigns{
                id
                name
              }
            }
          }
        `,
        variables: {
          data: {},
        },
      });
      commit('setNotifications', data.allNotifications);
    } catch (err) {
      console.error(err);
    }
  },
};
